<script lang="ts">
import AttachUploader from '@/app/common/components/AttachUploader.vue';
import { BAGGAGE_OPTIONS, STEP_THREE, LANG_EN, REFUND_OPTIONS, STEP3_SECTION, ATTACH_TYPE, FILE_COUNT_LIMIT, TITLE_OPTIONS } from '@/app/const';
import { BaseAlert } from '@/interface/BaseAlert.interface';
import { ComplaintClaim, ComplaintInfo } from '@/interface/Complaint.interface';
import { FormStep, FormStepItem } from '@/interface/Form.interface';
import CommonService from '@/services/commonService';
import { complaintService, fileService, formService, userService } from '@/services/serviceProvider';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

export default {
    components: {
        AttachUploader
    },
    setup() {
        const { t } = useI18n();
        const fileSv = fileService;
        const userSv = userService;
        const commonSv = new CommonService(t);
        const otherAttach = ref();

        return { fileSv, userSv, commonSv, otherAttach }
    },
    props: {
        modelValue: {
            type: Object,
            default: {},
        },
        viewMode: {
            type: Boolean,
            default: false
        },
        summaryComplaint: {
            type: Boolean,
            default: false
        },
        showTitle: {
            type: Boolean,
            default: true
        },
        showSectionTitle: {
            type: Boolean,
            default: true
        }, // for form management
    },
    emits: [
        'update:modelValue',
        'resetValidation',
        'displayAlertDialog',
    ],
    data() {
        return {
            model: this.modelValue as ComplaintInfo,
            airlineNotifyDatePicker: false as boolean,
            activeClaimOption: -1 as number,
            stepThree: STEP_THREE,
            formStepComplaint: [] as FormStepItem[],
            baggageOptions: BAGGAGE_OPTIONS,
            refundOptions: REFUND_OPTIONS,
            fileCountLimit: FILE_COUNT_LIMIT,
            titleOptions: TITLE_OPTIONS,
            selectedHour: null as string | null, // Selected hour
            selectedMinute: null as string | null, // Selected minute
            hourOptions: [] as string[], // List of hours
            minuteOptions: [] as string[], // List of minutes
            claimsRemarkModel: undefined as string | undefined,
            attachType: ATTACH_TYPE,
            rules: {
                required: (message: any) => {
                    return (v: any) => !!v || message;
                },
                minLengthRule(message: any) {
                    return (v: any) => (v && v.length >= 10) || message;
                },
                emailRule(message: any) {
                    return (v: any) =>
                        !!v.match(
                            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
                        ) || message;
                },
            },
            lang: this.$i18n.locale,
            en: LANG_EN,
            // value: null as any
        }
    },
    watch: {
        modelValue(newVal, oldVal) {
            if (newVal) {
                this.model = newVal;
            }
        },
        'model.detail.airlineNotify': function (newValue, prevValue) {
            if (newValue === false) {
                this.model.detail.airlineNotifyDate = undefined;
                this.model.detail.airlineNotifyDateString = undefined;
                this.model.detail.airlineNotifyTime = undefined;
                this.model.detail.airlineNotifyAttachUploads = [];
                this.model.detail.airlineNotifyAttachments = [];
                this.$emit('resetValidation');
            }
        },
        'model.detail.airlineNotifyDate': function (newValue, prevValue) {
            if (newValue) {
                this.model.detail.airlineNotifyDateString = complaintService.dateString(newValue);
            }
        },
        'model.detail.notifyAirline': function (newValue, prevValue) {
            if (newValue === false) {
                this.model.detail.notifyNumber = undefined;
                this.$emit('resetValidation');
            }
        },
        'model.form.step3Model': {
            handler(newValue: any) {
                if (newValue && this.model.form && this.model.form.step3Model[this.stepThree.Request - 1] === false) {
                    const ids = this.stepRequest.map(o => o.id);
                    const idRequest = this.stepRequest.find(o => o.name === STEP3_SECTION.REQUEST)?.id;
                    ids.forEach((id: number) => {
                        if (this.model.form && id != idRequest)
                            this.model.form.step3Model[id - 1] = false
                    });
                }
            }, deep: true
        },
        selectedHour(newVal, oldVal) {
            this.model.detail.airlineNotifyTime = this.selectedTime;
        },
        selectedTime(newVal, oldVal) {
            this.model.detail.airlineNotifyTime = this.selectedTime;
        },
        '$i18n.locale': function (newVal, oldVal) {
            this.lang = newVal;
        }
    },
    computed: {
        sections(): number[] {
            return this.model.form?.stepThree ?? [];
        },
        headers() {
            return [
                { title: this.$t('t-order'), sortable: false, key: 'index', align: 'center', width: '5%' },
                { title: this.$t('t-file-name'), key: 'fileName' },
                { title: '', key: 'actions', sortable: false, width: '2%' },
            ] as any[];
        },
        topSectionIds(): number[] {
            return this.formStepComplaint.filter(o => !o.name.startsWith(STEP3_SECTION.ATTACH)
                && !o.name.startsWith(STEP3_SECTION.REQUEST)).map(m => m.id);
        },
        hasTopSection(): boolean {
            return this.sections.some(this.isTopSection);
        },
        stepRequest() {
            return this.formStepComplaint.filter(f => f.name.startsWith(STEP3_SECTION.REQUEST) && f.name !== STEP3_SECTION.REQUEST);
        },
        selectedTime() {
            if (this.selectedHour !== null && this.selectedMinute !== null) {
                return `${this.selectedHour}:${this.selectedMinute}`;
            }
            return undefined;
        },
        claimsRemark(): string | undefined {
            return this.model.detail.claims?.find(o => o.claimsNo === STEP_THREE.RequestOther)?.remark?.toString()?.replaceAll("<br>", "\n")
        }
    },
    async mounted() {
        let time = this.model.detail.airlineNotifyTime?.split(":");
        if (time && time.length === 2) {
            this.selectedHour = time[0].toString();
            this.selectedMinute = time[1].toString();
        }
        await this.getFormStepComplaint();
        if (this.summaryComplaint)
            this.claimsRemarkModel = this.claimsRemark;
    },
    created() {
        this.generateTimeOptions();
    },
    methods: {
        async getFormStepComplaint() {
            try {
                const res = await formService.getAllSteps();
                if (res && res.status == 200) {
                    const result = res.data as FormStep;
                    this.formStepComplaint = result.stepThree;
                }
            } catch (e: any) {
                throw e;
            }
        },
        isTopSection(id: number) {
            return this.topSectionIds.includes(id);
        },
        enableSection(code: number) {
            return this.sections.includes(code);
        },
        baggageLabel(index: number) {
            return this.$t(`t-${this.baggageOptions[index].label}`);
        },
        refundLabel(index: number) {
            return this.$t(`t-${this.refundOptions[index].label}`);
        },
        allAttachments(): any[] {
            let newUploads = this.model.detail.attachUploads as any[] ?? [];
            newUploads.forEach(up => { up.type = ATTACH_TYPE.NEW });
            let alreadySaved = this.model.detail.attachments as any[] ?? [];
            alreadySaved.forEach(saved => { saved.type = ATTACH_TYPE.SAVED });
            return newUploads.concat(alreadySaved);
        },
        deleteAttachment(attach: any) {
            if (this.model.detail) {
                let allAttachs = this.allAttachments();
                if (allAttachs && allAttachs.length > 0) {
                    if (attach.type === ATTACH_TYPE.NEW) {
                        let newUploads = this.model.detail.attachUploads;
                        this.model.detail.attachUploads = newUploads?.filter(o => o.name !== attach.name);
                    } else if (attach.type === ATTACH_TYPE.SAVED) {
                        let saved = this.model.detail.attachments;
                        this.model.detail.attachments = saved?.filter(o => o.fileName !== attach.fileName);
                    }
                }
            }
        },
        allAirlineNotifyAttachments(): any[] {
            let newUploads = this.model.detail.airlineNotifyAttachUploads as any[] ?? [];
            newUploads.forEach(up => { up.type = ATTACH_TYPE.NEW });
            let alreadySaved = this.model.detail.airlineNotifyAttachments as any[] ?? [];
            alreadySaved.forEach(saved => { saved.type = ATTACH_TYPE.SAVED });
            return newUploads.concat(alreadySaved);
        },
        deleteAirlineNotifyAttachment(attach: any) {
            if (this.model.detail) {
                let allAttachs = this.allAirlineNotifyAttachments();
                if (allAttachs && allAttachs.length > 0) {
                    if (attach.type === ATTACH_TYPE.NEW) {
                        let newUploads = this.model.detail.airlineNotifyAttachUploads;
                        this.model.detail.airlineNotifyAttachUploads = newUploads?.filter(o => o.name !== attach.name);
                    } else if (attach.type === ATTACH_TYPE.SAVED) {
                        let saved = this.model.detail.airlineNotifyAttachments;
                        this.model.detail.airlineNotifyAttachments = saved?.filter(o => o.fileName !== attach.fileName);
                    }
                }
            }
        },
        claimLabel(claim: number) {
            if (this.formStepComplaint.find(o => o.id === claim))
                return this.$t(`t-${this.formStepComplaint.find(o => o.id === claim)?.name}`)
            return '';
        },
        isNumber(event: any) {
            // Check if the key pressed is a number, prevent default if not
            const charCode = event.which ? event.which : event.keyCode;
            if (
                charCode !== 46 && // allow period (.)
                charCode !== 44 && // allow comma (,)
                charCode > 31 && (charCode < 48 || charCode > 57) // block non-numeric characters
            ) {
                event.preventDefault();
            }
        },
        isViewDoc() {
            return this.viewMode && this.summaryComplaint;
        },
        isManage() {
            return this.viewMode && !this.summaryComplaint;
        },
        onClaimChanged() {
            if (!this.viewMode) {
                this.model.detail.claims = [];
                this.stepRequest?.forEach((o, index) => {
                    if (this.model.detail.claimsModel && this.model.detail.claimsModel[index].active) {
                        this.model.detail.claims?.push({
                            claimsNo: o.id,
                            remark: this.model.detail.claimsModel[index].remark
                        } as ComplaintClaim)
                    }
                })
            }
        },
        checkDuplicateFileName() {
            let fileNameList = [] as string[];
            let allAttach = this.allAttachments();
            let allAirlineNotifyAttach = this.allAirlineNotifyAttachments();
            let allFilesTopic = allAttach.concat(allAirlineNotifyAttach).map(o => {
                if (o.type === ATTACH_TYPE.NEW) {
                    return o.name;
                } else if (o.type === ATTACH_TYPE.SAVED) {
                    return o.fileName;
                }
            })
            allFilesTopic.forEach(fileName => {
                if (fileNameList.includes(fileName)) {
                    this.$emit('displayAlertDialog', {
                        messageTitle: this.$t('t-cannot-duplicate-file-name'),
                        messageText: this.$t('t-please-change-file-name'),
                    } as BaseAlert);
                    let index = this.model.detail.attachUploads?.findIndex(o => o.name === fileName) as number;
                    if (index >= 0) {
                        this.model.detail.attachUploads?.splice(index, 1);
                    } else {
                        index = this.model.detail.airlineNotifyAttachUploads?.findIndex(o => o.name === fileName) as number;
                        this.model.detail.airlineNotifyAttachUploads?.splice(index, 1);
                    }
                    return;
                } else {
                    fileNameList.push(fileName);
                }
            })
        },
        checkAllFilesSizelimit() {
            let allAttach = this.model.detail.attachUploads ? [...this.model.detail.attachUploads] : [];
            const totalSize = allAttach.reduce((sum, item) => sum + parseInt(item.size), 0);
            if (totalSize > 50000000) {
                let index = allAttach.length - 1;
                this.model.detail.attachUploads?.splice(index, 1);
                this.otherAttach.removeFile(index);
                this.$emit('displayAlertDialog', {
                    header: ``,
                    messageTitle: this.$t('t-cannot-upload-over-50'),
                    messageText: this.$t('t-please-select-another-file'),
                } as BaseAlert);
            }
        },
        overSizeLimit() {
            this.$emit('displayAlertDialog', {
                messageTitle: this.$t('t-cannot-upload-over-size-limit'),
                messageText: this.$t('t-please-select-another-file'),
            } as BaseAlert);
        },
        errorEmptyFile() {
            this.$emit('displayAlertDialog', {
                display: true,
                messageTitle: this.$t("t-attachment-error-0byte"),
                messageText: this.$t('t-please-select-another-file'),
            } as BaseAlert);
        },
        filterPhoneInput(value: string | undefined | null) {
            this.model.complainant.phoneNumber = value?.replace(/[^0-9]/g, ""); // Strip out non-numeric characters
        },
        generateTimeOptions() {
            // Generate hours (00-23)
            this.hourOptions = Array.from({ length: 24 }, (_, i) =>
                i.toString().padStart(2, "0")
            );

            // Generate minutes (00-59)
            this.minuteOptions = Array.from({ length: 60 }, (_, i) =>
                i.toString().padStart(2, "0")
            );
        },
        getFileIcon(fileName: string): string {
            const ext = fileName?.split('.').pop()?.toLowerCase();

            switch (ext) {
                case 'jpg':
                case 'jpeg':
                case 'png':
                case 'gif':
                case 'bmp':
                    return 'mdi-file-image-outline';

                case 'doc':
                case 'docx':
                    return 'mdi-microsoft-word';

                case 'xls':
                case 'xlsx':
                    return 'mdi-microsoft-excel';

                case 'pdf':
                    return 'mdi-file-pdf-box';

                case 'txt':
                case 'csv':
                    return 'mdi-file-document-outline';

                default:
                    return 'mdi-file-outline';
            }
        }
    },
};
</script>
<template>
    <v-card class="app-card" :class="isManage() ? 'hide-card' : ''">
        <v-toolbar v-if="isViewDoc() || (isManage() && showSectionTitle)" density="compact">
            <div class="w-100 d-flex justify-space-between">
                <v-toolbar-title>{{ $t('t-complaints') }}</v-toolbar-title>
                <v-chip variant="flat" class="gradient">
                    {{ lang === en ? model.subTopicEn : model.subTopicTh }}
                </v-chip>
            </div>
        </v-toolbar>
        <v-divider v-if="isViewDoc() || (isManage() && showSectionTitle)"></v-divider>
        <v-card-text :class="isManage() && showSectionTitle ? 'pb-0' : ''"
            :style="{ 'padding': ((isManage() || isViewDoc()) && !showSectionTitle) || !viewMode ? '0 !important' : '' }">
            <!-- Top Section (Complaint Detail)-->
            <v-card v-if="hasTopSection" class="inner-card"
                :class="isManage() && showSectionTitle ? 'no-shadow mb-0' : isManage() && !showSectionTitle ? 'hide-card' : 'hide-card'">
                <v-toolbar v-if="!viewMode || showTitle" style="width: fit-content;" density="compact">
                    <v-toolbar-title>{{ $t('t-complaint-info') }}</v-toolbar-title>
                </v-toolbar>
                <v-divider v-if="!viewMode || showTitle"></v-divider>
                <v-card-text :style="{ 'padding-top': isViewDoc() ? '0 !important' : '' }" class="topic-detail">
                    <v-row v-if="enableSection(stepThree.AirlineNotified)">
                        <v-col cols="12" sm="8" md="8" lg="8" class="d-flex align-center">
                            <div class="text-label mb-0 mr-2">{{ $t('t-complaint-airline-notify') }} *</div>
                            <div v-if="viewMode" class="d-flex align-center justify-end">
                                <div v-if="model.detail.airlineNotify" class="d-flex align-center">
                                    <v-icon icon="mdi mdi-check" color="success" class="mr-2"></v-icon>
                                    <span>{{ $t('t-complaint-notified') }}</span>
                                </div>
                                <div v-else class="d-flex align-center">
                                    <v-icon icon="mdi mdi-close" color="error" class="mr-2"></v-icon>
                                    <span>{{ $t('t-complaint-not-notify-yet') }}</span>
                                </div>
                            </div>
                        </v-col>
                        <v-col cols="12" sm="4" md="4" lg="4" class="radio-right pb-0">
                            <v-radio-group v-if="!viewMode" v-model="model.detail.airlineNotify" :readonly="viewMode"
                                :rules="model.detail.airlineNotify === null ? [rules.required(`${$t('t-please-input')}`)] : []"
                                hide-details="auto" inline>
                                <v-radio :label="$t('t-complaint-notified')" :value="true" class="mr-6"></v-radio>
                                <v-radio :label="$t('t-complaint-not-notify-yet')" :value="false"></v-radio>
                            </v-radio-group>
                            <!-- <div v-if="viewMode" class="d-flex justify-end">
                                <div v-if="model.detail.airlineNotify">
                                    <v-icon icon="mdi mdi-check" color="success" class="mr-2"></v-icon>
                                    <span>{{ $t('t-complaint-notified') }}</span>
                                </div>
                                <div v-else>
                                    <v-icon icon="mdi mdi-close" color="error" class="mr-2"></v-icon>
                                    <span>{{ $t('t-complaint-not-notify-yet') }}</span>
                                </div>
                            </div> -->
                        </v-col>
                    </v-row>
                    <v-card v-if="enableSection(stepThree.AirlineNotified) && model.detail.airlineNotify"
                        class="inner-card mt-4 pb-3 no-shadow" style="margin-left: -6px;"
                        :class="isManage() ? 'mb-0' : ''">
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" sm="8" md="8" lg="8" class="d-flex align-center pb-1">
                                    <div class="text-label mb-0">{{ $t('t-complaint-airline-notify-date-time') }} *
                                    </div>
                                </v-col>
                                <v-col cols="12" sm="2" md="2" lg="2" class="pb-1">
                                    <v-menu v-model="airlineNotifyDatePicker" :close-on-content-click="false"
                                        transition="scale-transition" class="list-modal">
                                        <template #activator="{ props }">
                                            <v-text-field v-model="model.detail.airlineNotifyDateString" readonly
                                                class="hide-float-label" v-bind="props" :clearable="!viewMode"
                                                append-inner-icon="mdi-calendar-blank-outline" density="comfortable"
                                                :label="$t('t-please-select')" hide-details="auto" variant="outlined"
                                                :rules="model.detail.airlineNotify ? [rules.required(`${$t('t-please-fill')}${$t('t-complaint-airline-notify-date')}`)] : []"></v-text-field>
                                        </template>
                                        <v-date-picker v-if="!viewMode" v-model="model.detail.airlineNotifyDate"
                                            elevation="24" :hide-header="true" :max="new Date()"
                                            @update:model-value="airlineNotifyDatePicker = false"></v-date-picker>
                                    </v-menu>
                                </v-col>
                                <v-col cols="12" sm="2" md="2" lg="2">
                                    <!-- <v-text-field v-model="model.detail.airlineNotifyTime" :label="$t('t-please-fill')"
                                        v-maska="'##:##'" append-inner-icon="mdi-clock-time-four-outline"
                                        placeholder="HH:mm" class="hide-float-label" variant="outlined"
                                        density="comfortable" hide-details="auto"
                                        :rules="model.detail.airlineNotify ? [rules.required(`${$t('t-please-fill')}${$t('t-complaint-airline-notify-time')}`)] : []">
                                    </v-text-field> -->
                                    <div class="d-flex align-center">
                                        <!-- Hour Dropdown -->
                                        <v-select v-model="selectedHour" :items="hourOptions" :readonly="viewMode"
                                            :placeholder="$t('t-hours')" class="mr-3 hide-float-label"
                                            variant="outlined" density="comfortable" hide-details="auto"></v-select>

                                        <!-- Minute Dropdown -->
                                        <v-select v-model="selectedMinute" :items="minuteOptions" :readonly="viewMode"
                                            :placeholder="$t('t-minutes')" class="hide-float-label" variant="outlined"
                                            density="comfortable" hide-details="auto"></v-select>
                                    </div>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col v-if="!summaryComplaint" cols="12">
                                    <v-row>
                                        <v-col cols="12" class="pb-0 pl-3">
                                            <div class="text-label mb-0">{{ $t('t-complaint-airline-notify-attach') }}
                                            </div>
                                        </v-col>
                                        <v-col cols="12" style="min-height: 130px;">
                                            <AttachUploader v-model="model.detail.airlineNotifyAttachUploads"
                                                fileType="attach"
                                                :disabled="viewMode || allAirlineNotifyAttachments().length >= fileCountLimit"
                                                @on-upload-success="checkDuplicateFileName"
                                                @on-over-size-limit="overSizeLimit"
                                                @on-error-empty-file="errorEmptyFile">
                                            </AttachUploader>
                                        </v-col>
                                        <v-col cols="12">
                                            <v-alert
                                                v-if="allAirlineNotifyAttachments() && allAirlineNotifyAttachments().length > 0"
                                                border="start" border-color="primary" :title="$t('t-upload-file-limit')"
                                                density="compact" variant="tonal" color="primary">
                                                <template v-slot:text>
                                                    <div class="d-flex flex-column">
                                                        <span>{{ $t('t-attachment-message') }}</span>
                                                        <span v-if="lang === en">
                                                            {{ $t('t-attachment-message-sub') }}
                                                        </span>
                                                    </div>
                                                </template>
                                            </v-alert>
                                        </v-col>
                                        <v-col cols="12" sm="4" md="4" lg="4"
                                            v-for="(attach, index) in allAirlineNotifyAttachments()">
                                            <div class="attach-items-display mb-2 py-3 px-3">
                                                <v-row>
                                                    <v-col cols="2" class="file-icon d-flex align-center justify-center"
                                                        @click="fileSv.viewFileInEditMode(attach)"
                                                        style="cursor: pointer">
                                                        <v-icon icon="mdi mdi-file-outline" size="xx-large"></v-icon>
                                                    </v-col>
                                                    <v-col cols="8">
                                                        <div class="d-flex flex-column">
                                                            <span v-if="fileSv.isNewAttachment(attach.type)">
                                                                {{ attach.name }}
                                                            </span>
                                                            <span v-if="fileSv.isSavedAttachment(attach.type)">
                                                                {{ attach.fileName }}
                                                            </span>
                                                            <span style="color: lightgray">
                                                                {{ fileSv.formatFileSize(Number(attach.size)) }}
                                                            </span>
                                                        </div>
                                                    </v-col>
                                                    <v-col cols="2" class="d-flex align-start justify-end">
                                                        <v-btn icon="mdi mdi-close" style="color: red"
                                                            @click="deleteAirlineNotifyAttachment(attach)"></v-btn>
                                                    </v-col>
                                                </v-row>
                                            </div>
                                        </v-col>
                                    </v-row>
                                </v-col>
                                <v-col v-else cols="12">
                                    <v-data-table :headers="headers" :items="model.detail.airlineNotifyAttachments"
                                        hide-default-footer :no-data-text="$t('t-no-data-available')"
                                        :items-per-page="-1">
                                        <template #[`item.index`]="{ index }">
                                            <div class="w-100 text-center">{{ index + 1 }}</div>
                                        </template>
                                        <template v-slot:item.actions="{ item }">
                                            <div class="actions d-flex align-center">
                                                <v-btn variant="text" color="primary"
                                                    @click="fileSv.viewFileAlreadySaved(item.url, item.fileName)">
                                                    <v-icon icon="mdi mdi-eye-outline" class="mr-1"></v-icon>
                                                    <span>{{ $t('t-view-doc') }}</span>
                                                </v-btn>
                                            </div>
                                        </template>
                                    </v-data-table>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                    <v-row v-if="enableSection(stepThree.DelayHour)" class="d-flex align-center">
                        <v-col cols="9" class="pb-0">
                            <div class="text-label">{{ $t('t-complaint-airline-hours-delay') }}</div>
                        </v-col>
                        <v-col cols="3" class="d-flex justify-end pb-0">
                            <span class="delay-hour" v-if="model.flight?.flightInfo?.delayString">
                                <span class="mr-2">
                                    {{ commonSv.timeString(model.flight.flightInfo.delayString) }}
                                </span>
                            </span>
                        </v-col>
                    </v-row>
                    <v-row v-if="enableSection(stepThree.Baggage)" class="d-flex align-center">
                        <v-col cols="12" class="pb-0">
                            <div class="text-label">{{ $t('t-complaint-request-reason') }} *</div>
                            <v-radio-group v-model="model.detail.baggageComplaint" :readonly="viewMode" hide-details>
                                <v-row>
                                    <v-col cols="6" class="pb-0">
                                        <v-radio :value="baggageOptions[0].value"
                                            class="border px-2 channel-box baggage-box"
                                            :class="model.detail.baggageComplaint === baggageOptions[0].value ? 'active' : ''">
                                            <template v-slot:label>
                                                <div class="pl-2">{{ baggageLabel(0) }}</div>
                                            </template>
                                        </v-radio>
                                    </v-col>
                                    <v-col cols="6" class="pb-0">
                                        <v-radio :value="baggageOptions[1].value"
                                            class="border px-2 channel-box baggage-box"
                                            :class="model.detail.baggageComplaint === baggageOptions[1].value ? 'active' : ''">
                                            <template v-slot:label>
                                                <div class="pl-2">{{ baggageLabel(1) }}</div>
                                            </template>
                                        </v-radio>
                                    </v-col>
                                    <v-col cols="6">
                                        <v-radio :value="baggageOptions[2].value"
                                            class="border px-2 channel-box baggage-box"
                                            :class="model.detail.baggageComplaint === baggageOptions[2].value ? 'active' : ''">
                                            <template v-slot:label>
                                                <div class="pl-2">{{ baggageLabel(2) }}</div>
                                            </template>
                                        </v-radio>
                                    </v-col>
                                    <v-col cols="6">
                                        <v-radio :value="baggageOptions[3].value"
                                            class="border px-2 channel-box baggage-box"
                                            :class="model.detail.baggageComplaint === baggageOptions[3].value ? 'active' : ''">
                                            <template v-slot:label>
                                                <div class="pl-2">{{ baggageLabel(3) }}</div>
                                            </template>
                                        </v-radio>
                                    </v-col>
                                </v-row>
                            </v-radio-group>
                        </v-col>
                    </v-row>
                    <v-row v-if="enableSection(stepThree.NeedRefundForService)" class="d-flex align-center">
                        <v-col cols="12">
                            <div class="text-label">{{ $t('t-complaint-request-reason') }} *</div>
                            <v-radio-group v-model="model.detail.refundService" :readonly="viewMode" hide-details>
                                <v-row>
                                    <v-col cols="12" class="pb-0">
                                        <v-radio :value="refundOptions[0].value"
                                            class="border px-2 channel-box baggage-box"
                                            :class="model.detail.refundService === refundOptions[0].value ? 'active' : ''">
                                            <template v-slot:label>
                                                <div class="pl-2">{{ refundLabel(0) }}</div>
                                            </template>
                                        </v-radio>
                                    </v-col>
                                </v-row>
                            </v-radio-group>
                        </v-col>
                    </v-row>
                    <v-row v-if="enableSection(stepThree.NeedDetail)">
                        <v-col cols="12">
                            <div class="text-label">{{ $t('t-complaint-remark') }} *</div>
                            <v-textarea v-model="model.detail.detail"
                                v-bind:placeholder="$t('t-complaint-search-input-text')" variant="outlined"
                                class="hide-float-label" :readonly="viewMode" hide-details="auto" no-resize auto-grow
                                rows="8" :rules="!summaryComplaint ? [rules.required(`${$t('t-please-input')}`)] : []">
                            </v-textarea>
                            <div class="textarea-export formatted-new-line mt-2" style="display: none;">
                                {{ model.detail.detail ?? '-' }}
                            </div>
                        </v-col>
                    </v-row>
                    <v-row v-if="enableSection(stepThree.NotifiedAirline)">
                        <v-col cols="12" sm="9" md="9" lg="9" class="d-flex align-center"
                            :class="!viewMode ? 'py-0' : ''">
                            <div class="text-label mr-2" :class="viewMode ? 'mb-0' : ''">
                                {{ $t('t-card-reported-to-airline') }} *
                            </div>
                            <div v-if="viewMode" class="d-flex align-center justify-end">
                                <div v-if="model.detail.notifyAirline" class="d-flex align-center">
                                    <v-icon icon="mdi mdi-check" color="success" class="mr-2"></v-icon>
                                    <span>{{ $t('t-yes') }}</span>
                                </div>
                                <div v-else class="d-flex align-center">
                                    <v-icon icon="mdi mdi-close" color="error" class="mr-2"></v-icon>
                                    <span>{{ $t('t-no') }}</span>
                                </div>
                            </div>
                        </v-col>
                        <v-col cols="12" sm="3" md="3" lg="3" class="radio-right py-0">
                            <v-radio-group v-if="!viewMode" v-model="model.detail.notifyAirline" :readonly="viewMode"
                                :rules="model.detail.notifyAirline === null ? [rules.required(`${$t('t-please-input')}`)] : []"
                                hide-details="auto" inline>
                                <v-radio :label="$t('t-yes')" :value="true" class="mr-2"></v-radio>
                                <v-radio :label="$t('t-no')" :value="false"></v-radio>
                            </v-radio-group>
                            <!-- <div v-if="viewMode" class="d-flex justify-end">
                                <div v-if="model.detail.notifyAirline">
                                    <v-icon icon="mdi mdi-check" color="success" class="mr-2"></v-icon>
                                    <span>{{ $t('t-yes') }}</span>
                                </div>
                                <div v-else>
                                    <v-icon icon="mdi mdi-close" color="error" class="mr-2"></v-icon>
                                    <span>{{ $t('t-no') }}</span>
                                </div>
                            </div> -->
                        </v-col>
                    </v-row>
                    <v-row v-if="enableSection(stepThree.NotifiedAirline) && model.detail.notifyAirline">
                        <v-col cols="12" sm="9" md="9" lg="9" class="d-flex align-center">
                            <div class="text-label">{{ $t('t-complaint-notify-number') }} *</div>
                        </v-col>
                        <v-col cols="12" sm="3" md="3" lg="3">
                            <v-text-field v-model.trim="model.detail.notifyNumber" density="comfortable"
                                variant="outlined" hide-details="auto" class="hide-float-label"
                                :label="$t('t-please-input')" :readonly="viewMode"
                                :rules="model.detail.notifyAirline ? [rules.required(`${$t('t-please-fill')}${$t('t-flight-number')}`)] : []"></v-text-field>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
            <!-- Attachment -->
            <v-card v-if="enableSection(stepThree.Attachment)" class="inner-card"
                :class="!showSectionTitle ? 'hide-card' : ''">
                <div class="w-100 d-flex align-center justify-start">
                    <v-toolbar style="width: fit-content;" density="compact" class="mr-2">
                        <v-toolbar-title>{{ $t('t-complaint-attachment') }}</v-toolbar-title>
                    </v-toolbar>
                    <v-chip v-if="model.detail.attachments && model.detail.attachments?.length > 0" class="gradient"
                        variant="flat" density="compact">
                        {{ model.detail.attachments?.length }}
                        {{ $t('t-file') }}
                    </v-chip>
                </div>
                <v-divider v-if="!viewMode"></v-divider>
                <v-card-text>
                    <v-row v-if="!summaryComplaint">
                        <v-col cols="12" style="height: 130px;">
                            <AttachUploader ref="otherAttach" v-model="model.detail.attachUploads" fileType="attach"
                                :disabled="viewMode" :check-size-limit="false"
                                :accept-file="'image/*,application/pdf,video/*'"
                                @on-upload-success="checkDuplicateFileName(); checkAllFilesSizelimit();"
                                @on-error-empty-file="errorEmptyFile">
                            </AttachUploader>
                        </v-col>
                        <v-col cols="12">
                            <v-alert v-if="allAttachments() && allAttachments().length > 0" border="start"
                                border-color="primary" :title="$t('t-upload-all-file-size-limit')" density="compact"
                                variant="tonal" color="primary">
                                <template v-slot:text>
                                    <div class="d-flex flex-column">
                                        <span>{{ $t('t-attachment-message') }}</span>
                                        <span v-if="lang === en">{{ $t('t-attachment-message-sub') }}</span>
                                        <span style="font-size: 12px; font-weight: 600; opacity: 0.8;" class="mt-2">
                                            <v-icon class="mdi mdi-information-outline"></v-icon>
                                            {{ $t('t-attachment-message-limit') }}
                                        </span>
                                    </div>
                                </template>
                            </v-alert>
                        </v-col>
                        <v-col cols="12" sm="4" md="4" lg="4" v-for="(attach, index) in allAttachments()">
                            <div class="attach-items-display mb-2 py-3 px-3">
                                <v-row>
                                    <v-col cols="2" class="file-icon d-flex flex-column align-center justify-center"
                                        @click="fileSv.viewFileInEditMode(attach)" style="cursor: pointer">
                                        <v-icon
                                            :icon="getFileIcon(fileSv.isNewAttachment(attach.type) ? attach.name : attach.fileName)"
                                            size="xx-large"></v-icon>
                                    </v-col>
                                    <v-col cols="8">
                                        <div class="d-flex flex-column">
                                            <span v-if="fileSv.isNewAttachment(attach.type)">
                                                {{ attach.name }}
                                            </span>
                                            <span v-if="fileSv.isSavedAttachment(attach.type)">
                                                {{ attach.fileName }}
                                            </span>
                                            <div class="d-flex align-end mt-3">
                                                <span style="color: lightgray" class="mr-3">
                                                    {{ fileSv.formatFileSize(Number(attach.size)) }}
                                                </span>
                                                <v-chip v-if="attach.type === attachType.NEW" color="error"
                                                    class="app-chip" density="compact" variant="flat"
                                                    style="width: fit-content;">{{
                                                        $t('t-attachment-New') }}</v-chip>
                                            </div>
                                        </div>
                                    </v-col>
                                    <v-col cols="2" class="d-flex align-start justify-end">
                                        <v-btn icon="mdi mdi-close" style="color: red"
                                            @click="deleteAttachment(attach)"></v-btn>
                                    </v-col>
                                </v-row>
                            </div>
                        </v-col>
                    </v-row>
                    <v-row v-else>
                        <v-col cols="12">
                            <v-data-table :headers="headers" :items="model.detail.attachments" hide-default-footer
                                :no-data-text="$t('t-no-data-available')" :items-per-page="-1">
                                <template #[`item.index`]="{ index }">
                                    <div class="w-100 text-center">{{ index + 1 }}</div>
                                </template>
                                <template v-slot:item.actions="{ item }">
                                    <div class="actions d-flex align-center">
                                        <v-btn variant="text" color="primary"
                                            @click="fileSv.viewFileAlreadySaved(item.url, item.fileName)">
                                            <v-icon icon="mdi mdi-eye-outline" class="mr-1"></v-icon>
                                            <span>{{ $t('t-view-doc') }}</span>
                                        </v-btn>
                                    </div>
                                </template>
                            </v-data-table>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-card-text>
    </v-card>
    <!-- Request (Create/Edit complaint & Form Management)-->
    <v-card v-if="!summaryComplaint && enableSection(stepThree.Request)" class="app-card"
        :class="!showSectionTitle ? 'hide-card' : ''">
        <v-toolbar v-if="showSectionTitle" density="compact">
            <v-toolbar-title>{{ $t('t-complaint-request') }} *</v-toolbar-title>
        </v-toolbar>
        <v-divider v-if="showSectionTitle"></v-divider>
        <v-card-text>
            <div v-if="model.detail.claimsModel" v-for="(claim, index) in stepRequest" @click="onClaimChanged">
                <div v-if="enableSection(claim.id)"
                    class="d-flex flex-column justify-center border py-2 px-2 mb-2 request-box"
                    :class="model.detail.claimsModel[index].active ? 'active' : ''">
                    <div class="d-flex w-100">
                        <div class="d-flex align-center mr-3" :class="viewMode ? 'readonly w-50' : 'w-100'"
                            @click="model.detail.claimsModel[index].active ? model.detail.claimsModel[index].active = false : model.detail.claimsModel[index].active = true">
                            <v-checkbox-btn v-model="model.detail.claimsModel[index].active"
                                :label="claimLabel(stepRequest[index].id)" class="pe-2"></v-checkbox-btn>
                        </div>
                        <div v-if="viewMode" class="d-flex align-center justify-end w-50 pr-3">
                            <v-switch v-if="model.form && model.form.step3Model"
                                v-model="model.form.step3Model[claim.id - 1]" hide-details :disabled="model.form.step3Model[stepThree.Request - 1] === false
                                    || model.form.isMigration"></v-switch>
                        </div>
                    </div>
                    <div v-if="!viewMode && claim.id === stepThree.RequestOther"
                        class="d-flex flex-column align-start w-100 pa-3">
                        <div class="text-label mr-2">{{ $t('t-please-fill') }} *</div>
                        <v-textarea v-model="model.detail.claimsModel[index].remark" class="w-100 hide-float-label"
                            style="width: inherit;" variant="outlined" :label="$t('t-complaint-search-input-text')"
                            :disabled="!model.detail.claimsModel[index].active" hide-details="auto" :readonly="viewMode"
                            :rules="model.detail.claimsModel[index].active ? [rules.required(`${$t('t-please-fill')}${$t('t-complaint-request')}`)] : []"
                            rows="10" @update:model-value="onClaimChanged">
                        </v-textarea>
                    </div>
                </div>
            </div>
        </v-card-text>
    </v-card>
    <v-card v-if="!userSv.isUser() && !isManage() && model.isCreateByASC" class="app-card officer">
        <v-toolbar density="compact">
            <v-toolbar-title>{{ $t('t-for-officer') }}</v-toolbar-title>
        </v-toolbar>
        <v-divider></v-divider>
        <v-card-text>
            <div class="text-label mr-2">{{ $t('t-complaint-doc-from') }}</div>
            <v-text-field v-if="model" v-model="model.remark" variant="outlined"
                class="channel-selection hide-float-label" density="comfortable" :readonly="viewMode"
                v-bind:placeholder="$t('t-please-input')" hide-details="auto">
            </v-text-field>
            <v-card class="inner-card pb-3 mt-4 mb-0" style="box-shadow: none;">
                <v-toolbar class="mr-6" style="width: fit-content;" density="compact">
                    <v-toolbar-title class="ml-0">
                        {{ $t('t-complaint-complaintant-info') }}
                    </v-toolbar-title>
                </v-toolbar>
                <v-divider></v-divider>
                <v-card-text>
                    <v-row>
                        <v-col cols="12" sm="3" lg="3">
                            <div class="text-label">{{ $t('t-user-title') }} *</div>
                            <v-select v-model="model.complainant.title" class="hide-float-label" variant="outlined"
                                v-bind:placeholder="$t('t-please-select')" hide-details="auto" :items="titleOptions"
                                item-value="value" item-title="label" density="comfortable" :readonly="viewMode"
                                :rules="viewMode ? [] : [rules.required(`${$t('t-please-select')}${$t('t-user-title')}`)]">
                                <template v-slot:item="{ props, item }">
                                    <v-list-item v-bind="props" :title="$t(`t-${item.raw.label}`)"></v-list-item>
                                </template>
                                <template v-slot:selection="{ item }">
                                    {{ $t(`t-${item.raw.label}`) }}
                                </template>
                            </v-select>
                        </v-col>
                        <v-col cols="12" sm="9" lg="9">
                            <v-row>
                                <v-col cols="12" sm="4" lg="4">
                                    <div class="text-label">{{ $t('t-user-first-name') }} *</div>
                                    <v-text-field v-model.trim="model.complainant.firstName" density="comfortable"
                                        variant="outlined" hide-details="auto" class="hide-float-label"
                                        v-bind:placeholder="$t('t-please-input')" :readonly="viewMode"
                                        :rules="viewMode ? [] : [rules.required(`${$t('t-please-fill')}${$t('t-user-first-name')}`)]"></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="4" lg="4">
                                    <div class="text-label">{{ $t('t-user-middle-name') }}</div>
                                    <v-text-field v-model.trim="model.complainant.middleName" density="comfortable"
                                        variant="outlined" hide-details="auto" class="hide-float-label"
                                        :readonly="viewMode" v-bind:placeholder="$t('t-please-input')"></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="4" lg="4">
                                    <div class="text-label">{{ $t('t-user-last-name') }} *</div>
                                    <v-text-field v-model.trim="model.complainant.lastName" density="comfortable"
                                        variant="outlined" hide-details="auto" class="hide-float-label"
                                        v-bind:placeholder="$t('t-please-input')" :readonly="viewMode"
                                        :rules="viewMode ? [] : [rules.required(`${$t('t-please-fill')}${$t('t-user-last-name')}`)]"></v-text-field>
                                </v-col>
                            </v-row>
                        </v-col>
                        <v-col cols="12" sm="3" lg="6">
                            <div class="text-label">{{ $t('t-user-phone') }} *</div>
                            <v-text-field v-model.trim="model.complainant.phoneNumber" density="comfortable"
                                variant="outlined" hide-details="auto" class="hide-float-label"
                                v-bind:placeholder="$t('t-please-input')" :readonly="viewMode"
                                @input="filterPhoneInput(model.complainant.phoneNumber)" maxlength="10" :rules="viewMode ? [] : [rules.required(`${$t('t-please-fill')}${$t('t-user-phone')}`),
                                rules.minLengthRule($t('t-please-fill-correct-phone'))]">
                            </v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6" lg="6">
                            <div class="text-label">{{ $t('t-user-email-full') }} *</div>
                            <v-text-field v-model.trim="model.complainant.email" density="comfortable"
                                variant="outlined" hide-details="auto" class="hide-float-label"
                                v-bind:placeholder="$t('t-please-input')" :readonly="viewMode" :rules="viewMode ? [] : [rules.required(`${$t('t-please-fill')}${$t('t-user-email-full')}`),
                                rules.emailRule($t('t-please-fill-correct-email'))]">
                            </v-text-field>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-card-text>
    </v-card>
    <!-- Request (View complaint) -->
    <v-card v-if="summaryComplaint && enableSection(stepThree.Request)" class="request-selected-view px-4 py-2">
        <div class="d-flex flex-column w-100">
            <div class="d-flex align-center justify-space-between my-3">
                <div class="title d-flex align-center">
                    {{ $t('t-complaint-request') }}
                </div>
                <div class="d-flex align-center selected-item">
                    <v-chip v-for="claim in model.detail.claims" variant="flat" color="white" class="mr-2">
                        <v-icon style="display:none" class="mdi mdi-check-circle mr-2" color="success"></v-icon>
                        <div class="mr-1">{{ claimLabel(claim.claimsNo) }}</div>
                    </v-chip>
                </div>
            </div>
            <div class="d-flex align-center">
                <div v-if="model.detail.claims?.map(o => o.claimsNo).includes(stepThree.RequestOther)
                    && model.detail.claims.find(o => o.claimsNo === stepThree.RequestOther)?.remark"
                    style="font-weight: 300;" class="w-100">
                    <v-card class="inner-card w-100">
                        <v-card-text>
                            <div class="text-label">{{ $t('t-card-list-other-remark') }}</div>
                            <v-textarea v-model="claimsRemarkModel" class="hide-float-label" variant="outlined"
                                hide-details="auto" density="comfortable" auto-grow readonly>
                            </v-textarea>
                            <div class="textarea-export formatted-new-line mt-2" style="display: none;">
                                {{ claimsRemarkModel ?? '-' }}
                            </div>
                        </v-card-text>
                    </v-card>
                </div>
            </div>
        </div>
    </v-card>
</template>